import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useProject } from "../ProjectContext";
import {
  convex_hull_analysis_api,
  getGraphData_api,
  check_cluster_output_api,
  subscribe_task_stream
} from "../api/project.api";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from "recharts";
import { cluster_service_api } from "../api/project.api";

const normalizeClusterId = (id) =>
  !id ? "0" : id.toString().trim().toLowerCase().replace("cluster_", "");

const getClusterColor = (clusterId) => {
  const colors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7f50",
    "#a0522d",
    "#20b2aa",
    "#ff69b4",
    "#8a2be2",
    "#00bfff",
    "#b22222",
  ];
  if (clusterId.toLowerCase() === "noise") return "#ccc";
  const idx = parseInt(clusterId, 10) % colors.length;
  return colors[idx] || colors[0];
};

const Cluster = () => {
  const { user, setRedirectPath, userId } = useAuth();
  const { activeProject } = useProject();
  const navigate = useNavigate();

  const [clusterStatus, setClusterStatus] = useState("idle");
  const [infoMessage, setInfoMessage] = useState("");
  const [algorithm, setAlgorithm] = useState("K-means");
  const [reductionAlgo, setReductionAlgo] = useState("PCA");
  const [numClusters, setNumClusters] = useState("");
  const [epsilon, setEpsilon] = useState("");
  const [minPoints, setMinPoints] = useState("");
  const [scatterData, setScatterData] = useState([]);
  const [hullMetrics, setHullMetrics] = useState([]);
  const [showRadar, setShowRadar] = useState(false);
  const [rValue, setRValue] = useState(null);
  const [radarColor, setRadarColor] = useState("#F53838");
  const [clusterOutputExists, setClusterOutputExists] = useState(false); // ← Add state
  const [isCheckingExisting, setIsCheckingExisting] = useState(true); // ← Add loading state

  useEffect(() => {
    if (!user) {
      setRedirectPath("/cluster");
      navigate("/login");
    }
  }, [user, navigate, setRedirectPath]);

  // -------- Check for Existing Cluster Output on Mount --------
  useEffect(() => {
    const checkExistingClusterOutput = async () => {
      if (!userId || !activeProject?.ProjectID) {
        setIsCheckingExisting(false);
        return;
      }

      try {
        setIsCheckingExisting(true);
        const response = await check_cluster_output_api(userId, activeProject.ProjectID);

        // Check if cluster data exists
        if (response.clusters && response.clusters.length > 0) {
          // Restore cluster state
          const points = response.clusters.map((row, index) => {
            return {
              x: parseFloat(row.x),
              y: parseFloat(row.y),
              cluster: normalizeClusterId(row.cluster),
              text: row.text,
              index,
            };
          });
          setScatterData(points);
          setClusterStatus("clustered");
          setClusterOutputExists(true);

          // Check if convex hull analysis also exists
          if (response.convexHullAnalysis && (response.convexHullAnalysis.R && response.convexHullAnalysis.metrics)) {
            setHullMetrics(response.convexHullAnalysis.metrics);
            if (response.convexHullAnalysis.R !== null) {
              setRValue(response.convexHullAnalysis.R);
              // Calculate brightness factor based on R value
              const brightnessFactor = Math.max(0.2, Math.min(1, response.convexHullAnalysis.R));
              setRadarColor(adjustBrightness("#F53838", brightnessFactor));
              setShowRadar(true);
            }
            setInfoMessage("Loaded existing cluster analysis with convex hull metrics.");
          } else {
            setInfoMessage("Loaded existing cluster data.");
          }
        } else {
          // No cluster data found - fresh start
          setClusterStatus("idle");
          setClusterOutputExists(false);
          setScatterData([]);
          setHullMetrics([]);
          setRValue(null);
        }
      } catch (error) {
        // Don't show error to user - just start fresh
        setClusterStatus("idle");
        setClusterOutputExists(false);
        setScatterData([]);
        setHullMetrics([]);
        setRValue(null);
      } finally {
        setIsCheckingExisting(false);
      }
    };

    checkExistingClusterOutput();
  }, [userId, activeProject?.ProjectID]); // Re-check when user or project changes

  // -------- Reset state on project change --------
  // useEffect(() => {
  //   if (!isCheckingExisting) { // Only reset if not currently checking
  //     setClusterStatus("idle");
  //     setInfoMessage("");
  //     setScatterData([]);
  //     setHullMetrics([]);
  //     setShowRadar(false);
  //     setRValue(null);
  //     setClusterOutputExists(false);
  //   }
  // }, [activeProject, isCheckingExisting]);

  // ========== API Handlers ==========
  const handleRunClustering = async () => {
    setClusterStatus("clustering");
    setInfoMessage("Running clustering...");
    try {
      if (algorithm === "K-means" && (!numClusters || parseInt(numClusters) <= 0)) {
        alert("Please provide a valid number of clusters for K-means.");
        setInfoMessage("Please provide a valid number of clusters for K-means.");
        setClusterStatus("idle");
        return;
      } else if ((algorithm === "Dbscan" || algorithm === "H-Dbscan") && (!epsilon || parseFloat(epsilon) <= 0 || !minPoints || parseInt(minPoints) <= 0)) {
        alert("Please provide valid epsilon and min points for DBSCAN/H-DBSCAN.");
        setInfoMessage("Please provide valid epsilon and min points for DBSCAN/H-DBSCAN.");
        setClusterStatus("idle");
        return;
      }
      const start = await cluster_service_api(
        userId,
        activeProject.ProjectID,
        algorithm,
        reductionAlgo,
        numClusters,
        epsilon,
        minPoints
      );
      const taskId = start?.task_id;
      if (!taskId) throw new Error("No task id returned");

      // Clear previous visuals when (re)running clustering
      setScatterData([]);
      setHullMetrics([]);
      setRValue(null);
      setShowRadar(false);
      setClusterOutputExists(false);

      subscribe_task_stream(taskId, (evt) => {
        if (evt.state === 'PROCESSING') {
          setInfoMessage(evt.status || 'Running clustering...');
        }
        if (evt.state === 'SUCCESS') {
          setClusterStatus("clustered");
          setClusterOutputExists(true);
          setInfoMessage("Clustering complete! You can now generate graph and metrics.");
          activeProject.currentStage = "clustering";
        }
        if (evt.state === 'FAILURE') {
          setClusterStatus("idle");
          setClusterOutputExists(false);
          // Show specific error message from backend
          const errorMsg = evt.error || "Clustering failed.";
          setInfoMessage(errorMsg);
          // Show alert for important errors (missing files, etc.)
          if (errorMsg.includes("not found") || errorMsg.includes("Please run")) {
            alert(errorMsg);
          }
        }
      });
    } catch (error) {
      // Handle synchronous errors (like file not found pre-check)
      setClusterStatus("idle");
      setClusterOutputExists(false);
      const errorMessage = error.message || "Clustering failed.";
      setInfoMessage(errorMessage);
      
      // Show alert for missing file errors
      if (errorMessage.includes("not found") || errorMessage.includes("Please run")) {
        alert(errorMessage);
      }
    }
  };

  const handleGenerateGraph = async () => {
    setInfoMessage("Loading scatter data...");
    setScatterData([]);
    setShowRadar(false);
    try {
      const response = await getGraphData_api(userId, activeProject.ProjectID);
      const points = response.map((row, index) => {
        return {
          x: parseFloat(row.x),
          y: parseFloat(row.y),
          cluster: normalizeClusterId(row.cluster),
          text: row.text,
          index,
        };
      });
      setScatterData(points);
      setInfoMessage("Scatter plot generated.");
    } catch (err) {
      setInfoMessage("Failed to load cluster CSV.");
    }
  };

  const handleConvexHull = async () => {
    try {
      setInfoMessage("Computing convex hull metrics...");
      // Single API call for complete analysis
      const data = await convex_hull_analysis_api(userId, activeProject.ProjectID);
      // Set R value for visualization
      setRValue(data.R);
      // Calculate brightness factor based on R value
      const brightnessFactor = Math.max(0.2, Math.min(1, data.R));
      setRadarColor(adjustBrightness("#F53838", brightnessFactor));
      // Set metrics for table and spider plot
      setHullMetrics(data.metrics);
      setInfoMessage(
        `Convex hull metrics computed. R-value: ${data.R.toFixed(4)}`
      );
    } catch (error) {
      setInfoMessage(`Convex hull analysis failed: ${error.message}`);
    }
  };

  const handleReRunClustering = async () => {
    setScatterData([]);
    setHullMetrics([]);
    setRValue(null);
    setShowRadar(false);
    setClusterOutputExists(false);
    setClusterStatus("idle");
    setInfoMessage("Cleared clustering data. You can now run clustering again.");
  };

  // Helper function to adjust color brightness
  const adjustBrightness = (color, factor) => {
    const hex = color.replace("#", "");
    const r = Math.round(parseInt(hex.substr(0, 2), 16) * factor);
    const g = Math.round(parseInt(hex.substr(2, 2), 16) * factor);
    const b = Math.round(parseInt(hex.substr(4, 2), 16) * factor);
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  // ========== Loading State ==========
  if (isCheckingExisting) {
    return (
      <div className="flex flex-col h-full w-full bg-[#f9f9f9]">
        <div className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">Clustering & Convex Hull Analysis</h1>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#ff2e2e] mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Checking for existing cluster data...</p>
          </div>
        </div>
      </div>
    );
  }

  // ========== Render ==========
  return (
    <div className="flex flex-col h-full w-full bg-[#f9f9f9]">
      {/* Header */}
      <div className="w-full bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">
            Clustering & Convex Hull Analysis
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {!scatterData.length > 0 ? (
          // Original blocks view when no graph data
          <div className="h-full flex flex-col items-center p-4 sm:p-6 md:p-8 lg:p-10">
            {/* Two Column Grid - Stack vertically on mobile, side-by-side on lg+ */}
            <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-10 mb-6 sm:mb-8">
              {/* ========== LEFT BLOCK: Clustering Setup ========== */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 border border-gray-200 flex flex-col min-h-[400px] sm:min-h-[500px] lg:h-[55vh]">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">
                    Clustering Setup
                  </h2>
                  <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-4 py-2 rounded whitespace-nowrap">
                    Algorithm Configuration
                  </span>
                </div>

                {/* Algorithm Selection */}
                <label className="block text-gray-800 font-semibold mb-2">
                  Choose Clustering Algorithm
                </label>
                <select
                  value={algorithm}
                  onChange={(e) => setAlgorithm(e.target.value)}
                  className="w-full p-3 mb-4 text-base border border-gray-300 rounded-lg"
                >
                  <option value="K-means">K-means</option>
                  <option value="Dbscan">DBSCAN</option>
                  <option value="H-Dbscan">H-DBSCAN</option>
                </select>

                {algorithm === "K-means" && (
                  <input
                    type="number"
                    placeholder="Number of Clusters"
                    value={numClusters}
                    onChange={(e) => setNumClusters(e.target.value)}
                    className="w-full p-3 mb-4 text-base border border-gray-300 rounded-lg"
                  />
                )}
                {(algorithm === "Dbscan" || algorithm === "H-Dbscan") && (
                  <>
                    <input
                      type="number"
                      step="any"
                      placeholder="Epsilon"
                      value={epsilon}
                      onChange={(e) => setEpsilon(e.target.value)}
                      className="w-full p-3 mb-4 text-base border border-gray-300 rounded-lg"
                    />
                    <input
                      type="number"
                      placeholder="Min Points"
                      value={minPoints}
                      onChange={(e) => setMinPoints(e.target.value)}
                      className="w-full p-3 mb-4 text-base border border-gray-300 rounded-lg"
                    />
                  </>
                )}

                {/* Reduction Algorithm Radio */}
                <label className="block text-gray-800 font-semibold mb-2">
                  Choose Reduction Algorithm
                </label>
                <div className="flex gap-6 mb-6 flex-grow">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="reduction"
                      value="PCA"
                      checked={reductionAlgo === "PCA"}
                      onChange={(e) => setReductionAlgo(e.target.value)}
                      className="w-4 h-4 text-red-600"
                    />
                    <span>PCA</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="reduction"
                      value="UMAP"
                      checked={reductionAlgo === "UMAP"}
                      onChange={(e) => setReductionAlgo(e.target.value)}
                      className="w-4 h-4 text-red-600"
                    />
                    <span>UMAP</span>
                  </label>
                </div>

                {/* Run Clustering Button */}
                <button
                  className={`
                      w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                      font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center
                      bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                      hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                    `}
                  onClick={handleRunClustering}
                  disabled={clusterStatus === "clustering"}
                >
                  {clusterStatus === "idle" && "Run Clustering"}
                  {clusterStatus === "clustering" && "Clustering..."}
                  {clusterStatus === "clustered" && "Clustered ✓"}
                </button>
              </div>

              {/* ========== RIGHT BLOCK: Visualization & Metrics ========== */}
              <div
                className={`
                    bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 border border-gray-200 flex flex-col min-h-[400px] sm:min-h-[500px] lg:h-[55vh]
                    ${clusterStatus !== "clustered" ? 'opacity-50 pointer-events-none' : ''}
                  `}
              >
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0c0c1d]">
                    Visualization & Metrics
                  </h2>
                  {clusterStatus !== "clustered" && (
                    <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-4 py-2 rounded">
                      Locked
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-gray-700 text-sm sm:text-base mb-4 sm:mb-6 md:mb-8 leading-relaxed flex-grow">
                  Once clustering is complete, you can visualize the 2D scatter plot,
                  compute convex hull metrics, and view the spider plot for cluster
                  distribution analysis.
                </p>

                {/* Generate Graph Button */}
                <button
                  className={`
                      w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                      font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center mb-3 sm:mb-4
                      bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                      hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                    `}
                  onClick={handleGenerateGraph}
                  disabled={clusterStatus !== "clustered"}
                >
                  Generate Graph
                </button>

                {/* Analyze Clusters Button */}
                <button
                  className={`
                      w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                      font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center mb-3 sm:mb-4
                      bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                      hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                    `}
                  onClick={handleConvexHull}
                  disabled={clusterStatus !== "clustered"}
                >
                  Analyze Clusters
                </button>

                {/* Spider Plot Button */}
                <button
                  className={`
                      w-full px-4 sm:px-6 md:px-8 py-3 sm:py-4 border-none rounded-md cursor-pointer 
                      font-bold text-sm sm:text-base md:text-lg transition-all duration-200 text-center
                      bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                      hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                    `}
                  onClick={() => setShowRadar((v) => !v)}
                  disabled={clusterStatus !== "clustered"}
                >
                  {showRadar ? "Hide Spider Plot" : "Show Spider Plot"}
                </button>

                {clusterStatus !== "clustered" && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded text-base text-gray-600 text-center">
                    Complete clustering first to unlock visualization
                  </div>
                )}
              </div>
            </div>

            {/* Info Message */}
            {infoMessage && (
              <div className="w-full max-w-7xl">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded text-base text-blue-800 text-center">
                  {infoMessage}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Visualization view when data exists - similar to PCA's collapsed view
          <div className="w-full h-full flex flex-col">
            {/* Collapsed Buttons Row */}
            <div className="w-full bg-white border-b border-gray-200 px-4 sm:px-6 md:px-8 py-3 sm:py-4">
              <div className="max-w-7xl mx-auto flex flex-wrap gap-4 justify-center">
                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleReRunClustering}
                  disabled={clusterStatus === "clustering"}
                >
                  {clusterStatus === "idle" && "Run Clustering"}
                  {clusterStatus === "clustering" && "Clustering..."}
                  {clusterStatus === "clustered" && "Re-run Clustering "}
                </button>

                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleGenerateGraph}
                  disabled={clusterStatus !== "clustered"}
                >
                  Graph Generated ✓
                </button>

                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={handleConvexHull}
                  disabled={clusterStatus !== "clustered"}
                >
                  {hullMetrics.length === 0 && "Analyze Clusters"}
                  {hullMetrics.length > 0 && "Analyzed ✓"}
                </button>

                <button
                  className={`
                    px-6 py-3 border-none rounded-md cursor-pointer 
                    font-bold text-base transition-all duration-200 text-center
                    bg-[#ff2e2e] text-white shadow-[0_8px_20px_rgba(255,46,46,0.4)]
                    hover:scale-[1.03] hover:shadow-[0_10px_25px_rgba(255,46,46,0.6)]
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                  `}
                  onClick={() => setShowRadar((v) => !v)}
                  disabled={hullMetrics.length === 0}
                >
                  {showRadar ? "Hide Spider Plot" : "Show Spider Plot"}
                </button>
              </div>
            </div>

            {/* Visualization Content */}
            <div className="flex-1 overflow-auto p-8">
              <div className="max-w-7xl mx-auto space-y-8">
                {/* ========== SCATTER PLOT ========== */}
                <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-[#0c0c1d] mb-6">
                    Cluster Scatter Plot
                  </h3>
                  <div className="w-full" style={{ height: '500px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart
                        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                      >
                        <XAxis type="number" dataKey="x" name="X" />
                        <YAxis type="number" dataKey="y" name="Y" />
                        <Tooltip
                          cursor={{ strokeDasharray: "3 3" }}
                          content={({ active, payload }) => {
                            if (active && payload?.length) {
                              const p = payload[0].payload;
                              return (
                                <div
                                  style={{
                                    background: "#fff",
                                    padding: 8,
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <p>
                                    <strong>Cell #{p.index}</strong> — Cluster:{" "}
                                    {p.cluster}
                                  </p>
                                  <p>X: {p.x.toFixed(2)}</p>
                                  <p>Y: {p.y.toFixed(2)}</p>
                                  <p style={{ color: "#444" }}>{p.text}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Scatter
                          data={scatterData}
                          shape={({ payload, ...rest }) => (
                            <circle
                              {...rest}
                              r={4}
                              fill={getClusterColor(payload.cluster)}
                            />
                          )}
                        >
                          <LabelList
                            dataKey="index"
                            position="left"
                            style={{ fontSize: 8, fill: "#333", fontWeight: "bold" }}
                          />
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* ========== HULL METRICS TABLE ========== */}
                {hullMetrics.length > 0 && (
                  <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#0c0c1d] mb-6">
                      Convex Hull Metrics by Cluster
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-4 py-2 text-left">Cluster</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">Area</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">NA</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">NP</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">NA / NP</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">Sparsity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hullMetrics.map((row) => (
                            <tr key={row.cluster}>
                              <td className="border border-gray-300 px-4 py-2">{row.cluster}</td>
                              <td className="border border-gray-300 px-4 py-2">{Number(row.area).toExponential(2)}</td>
                              <td className="border border-gray-300 px-4 py-2">{Number(row.NA).toExponential(2)}</td>
                              <td className="border border-gray-300 px-4 py-2">{row.NP}</td>
                              <td className="border border-gray-300 px-4 py-2">{Number(row.naPerNp).toExponential(2)}</td>
                              <td className="border border-gray-300 px-4 py-2">{Number(row.sparsity).toExponential(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {rValue !== null && (
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                        <strong>R (Total Cluster Area / Overall Convex Hull Area):</strong>{" "}
                        {rValue.toFixed(4)}
                      </div>
                    )}
                  </div>
                )}

                {/* ========== RADAR/SPIDER PLOT ========== */}
                {showRadar && hullMetrics.length > 0 && (
                  <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#0c0c1d] mb-6">
                      Convex Hull Area Spider Plot
                    </h3>
                    <div className="w-full" style={{ height: '400px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart
                          cx="50%"
                          cy="50%"
                          outerRadius="80%"
                          data={hullMetrics}
                        >
                          <PolarGrid />
                          <PolarAngleAxis dataKey="cluster" />
                          <PolarRadiusAxis />
                          <Radar
                            name="Area"
                            dataKey="area"
                            stroke={radarColor}
                            fill={radarColor}
                            fillOpacity={0.6}
                          />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Info Message */}
                {infoMessage && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded text-base text-blue-800 text-center">
                    {infoMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cluster;